/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Backdrop,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import Topheader from "../../../../Layout/header/header";
import { ClearIcon } from "@mui/x-date-pickers";
import Sidebar from "../sidebar/sidebar";
import { useSelector, useDispatch } from "react-redux";
import "./RecommendedApps.css";
import APIManager from "../../../../Services/ApiManager/ApiManager";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import Collapse from "@mui/material/Collapse";
import PopupDialog from "../../../../Components/PopupDialog";
import Grid from "@mui/material/Grid2";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import Button from "@mui/material/Button";
import { SettingsFilterOemAdmin } from "../common/SettingsFilterOemAdmin";
import { fetchAdminCountriesDetails } from "../../../../Services/redux/Oem-admin-counties/OemAdminCountryDetailsActions";
import { fetchOemAdminEcuDetails } from "../../../../Services/redux/Ecu-types/OemAdminEcuActions";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import styles from "../../../../page/ApplicationList/ApplicationList.module.css";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addDays } from "date-fns";
import MenuItem from "@mui/material/MenuItem";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  createCenteredModalStyle,
  StyledMenu,
} from "../suggested-apps/StyledMenu";

function SelectAllPaperComponent({
  children,
  hasOptions,
  onToggleSelectAll,
  selectAllLabel,
  isSelectAll,
  onBeforeToggle,
  ...restProps
}) {
  return (
    <Paper {...restProps}>
      <>
        <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
          {" "}
          {hasOptions && (
            <FormControlLabel
              onClick={(e) => {
                if (onBeforeToggle) onBeforeToggle();
                e.preventDefault();
                onToggleSelectAll();
              }}
              label={selectAllLabel}
              control={
                <Checkbox id="select-all-checkbox" checked={isSelectAll} />
              }
            />
          )}
        </Box>
        <Divider />
      </>
      {children}
    </Paper>
  );
}

SelectAllPaperComponent.propTypes = {
  children: PropTypes.node,
  hasOptions: PropTypes.bool,
  onToggleSelectAll: PropTypes.func.isRequired,
  selectAllLabel: PropTypes.string.isRequired,
  isSelectAll: PropTypes.bool.isRequired,
  onBeforeToggle: PropTypes.func,
};

function itemMatchesAnyCountry(item, selectedCountryNames) {
  return item.countries?.some((country) =>
    selectedCountryNames.includes(country),
  );
}

function itemMatchesAnyEcu(item, selectedEcus) {
  return item.ecuTypes?.some((ecu) => selectedEcus.includes(ecu));
}

function createData(app) {
  return {
    id: app.id,
    icon: app.icon,
    applicationName: app.applicationName,
    publisher: app.publisher,
    startDate: app.startDate,
    endDate: app.endDate,
    countries: app.countries,
    ecuTypes: app.ecuTypes,
    recommendedApps: app.recommendedApps,
    packageId: app.packageId,
  };
}

function formatDateStr(timestamp) {
  const date = new Date(Number(timestamp));
  if (Number.isNaN(date.getTime())) return "Invalid";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function normalizeSortableValue(value, orderBy) {
  if (orderBy === "startDate" || orderBy === "endDate") {
    return Number(value) || 0;
  }

  if (typeof value === "string") {
    return value.toLowerCase();
  }

  return value ?? "";
}

function descendingComparator(a, b, orderBy) {
  const firstValue = normalizeSortableValue(a[orderBy], orderBy);
  const secondValue = normalizeSortableValue(b[orderBy], orderBy);

  if (secondValue < firstValue) {
    return -1;
  }
  if (secondValue > firstValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}


// CODE53: Get Pinned oder value for sorting pinned apps. If order is not set or is 0, return null to indicate no order.
// Related items (auto-generated):
// Parents:
//   SDD53MARKETPLACE1056R121: Recommended apps Ordering & Filtering https://github.com/HARMAN-Auto/sp-prod-marketplace-portal-frontend/blob/7ee8cffd2fd2c4f1de55e0d0ea3af5a4bb082f9d/docs/SDD/Global-Backend-OemAdmin-LLD.md#L3404
// End related items

function getPinnedOrderValue(row) {
  const value = Number(row?.recommendedApps?.order);
  return Number.isFinite(value) && value !== 0 ? value : null;
}

function itemMatchesStatus(item, selectedStatus) {
  if (!selectedStatus || selectedStatus === "ALL") {
    return true;
  }

  const start = Number(item?.recommendedApps?.startDate);
  const end = Number(item?.recommendedApps?.endDate);

  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return false;
  }

  const now = Date.now();

  if (selectedStatus === "ACTIVE") {
    return start <= now && end >= now;
  }

  if (selectedStatus === "EXPIRED") {
    return end < now;
  }

  if (selectedStatus === "FUTURE") {
    return start > now;
  }

  return true;
}

const sharedHeadCells = [
  {
    id: "icon",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
  },
  {
    id: "applicationName",
    numeric: false,
    disablePadding: false,
    label: "Application Name",
    sortable: true,
  },
  {
    id: "publisher",
    numeric: false,
    disablePadding: false,
    label: "Publisher",
    sortable: true,
  },
  {
    id: "startDate",
    numeric: true,
    disablePadding: false,
    label: "Start Date",
    sortable: true,
  },
  {
    id: "endDate",
    numeric: true,
    disablePadding: false,
    label: "End Date",
    sortable: true,
  },
];

const unpinnedHeadCells = [
  {
    id: "pin",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
  },
  ...sharedHeadCells,
];

const pinnedHeadCells = [
  {
    id: "drag",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
  },
  {
    id: "pin",
    numeric: false,
    disablePadding: false,
    label: "",
    sortable: false,
  },
  ...sharedHeadCells,
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, isPinnedTable = false } = props;
  const headCells = isPinnedTable ? pinnedHeadCells : unpinnedHeadCells;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className="tableHeaderCls">
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              backgroundColor: "#f1f1f1",
              borderBottom: "1px solid #d8d8d8",
              color: "#2d2d2d",
              fontSize: "13px",
              fontWeight: 700,
              padding:
                headCell.id === "pin" || headCell.id === "drag"
                  ? "12px 8px"
                  : "12px 14px",
              width:
                headCell.id === "pin" || headCell.id === "drag"
                  ? "40px"
                  : "auto",
            }}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : null}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  isPinnedTable: PropTypes.bool,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

function Row({ row, onEdit, onDelete, getCountryName, isPinned, onTogglePin }) {
  const [open, setOpen] = React.useState(false);
  const [openMoreCountry, setOpenMoreCountry] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: !isPinned });
  const openMenu = Boolean(anchorEl);
  let countriesArr = row?.recommendedApps?.appSearchInfo?.countries.includes(
    "ALL",
  )
    ? row?.countries
    : row?.recommendedApps?.appSearchInfo?.countries || [];
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const rowStyle = isPinned
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging
          ? {
              position: "relative",
              zIndex: 2,
              backgroundColor: "#ffffff",
            }
          : {}),
      }
    : undefined;

  return (
    <React.Fragment>
      <TableRow
        ref={isPinned ? setNodeRef : undefined}
        style={rowStyle}
        hover
        tabIndex={-1}
        key={row.id}
        className="recommended-apps-table-row"
        sx={{ cursor: "pointer" }}
      >
        {isPinned && (
          <TableCell
            component="th"
            scope="row"
            padding="normal"
            align="center"
            className="recommended-apps-drag-cell"
          >
            <IconButton
              size="small"
              sx={{ padding: 0 }}
              className="recommended-apps-drag-handle"
              {...attributes}
              {...listeners}
              aria-label="drag pinned app"
            >
              <DragIndicatorIcon className="recommended-apps-drag-icon" />
            </IconButton>
          </TableCell>
        )}
        <TableCell
          component="th"
          scope="row"
          padding="normal"
          align="center"
          className="recommended-apps-pin-cell"
        >
          <IconButton
            size="small"
            onClick={() => onTogglePin(row.id)}
            sx={{ padding: 0 }}
            aria-label={isPinned ? "unpin app" : "pin app"}
          >
            {isPinned ? (
              

 <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fillRule="evenodd" clipRule="evenodd" d="M25.6728 16.7117L26.793 15.5916L21.1921 9.99072L20.534 10.6488L20.534 20.7303L25.1127 16.1516L25.6728 16.7117Z" fill="#121415" fillOpacity="0.6"/>
<path d="M12.7909 23.9928L12.7909 22.8727L16.1514 19.5122L13.3509 16.7117L14.4711 15.5916L16.7115 15.5916L17.3556 14.9475L17.5775 9.83236L19.1458 9.83236L18.7018 25.8875L17.1056 25.8595L17.3556 20.7163L17.2715 20.6323L13.911 23.9928L12.7909 23.9928Z" fill="#121415" fillOpacity="0.6"/>
</svg>

            ) : (
             <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M20.8284 19.697L20.8284 21.9597L19.697 23.0911L16.8686 20.2627L13.4745 23.6568L12.3431 23.6568V22.5254L15.7372 19.1313L12.9088 16.3029L14.0402 15.1715L16.3029 15.1715L20.2627 11.2117L19.697 10.646L20.8284 9.51466L26.4852 15.1715L25.3539 16.3029L24.7882 15.7372L20.8284 19.697Z" fill="#121415" fillOpacity="0.6"/>
</svg>

            )}
          </IconButton>
        </TableCell>
        <TableCell align="left" className="recommended-apps-icon-cell">
          <img
            src={row.icon}
            alt={row.applicationName}
            width="36"
            height="36"
            className="recommended-apps-app-icon"
          />
        </TableCell>
        <TableCell align="left" className="recommended-apps-name-cell">
  {row.applicationName && (
            <Tooltip title={row.applicationName}>
              <Typography className="recommended-apps-app-name">
                {row.applicationName.length > 24
                  ? `${row.applicationName.substring(0, 21)}...`
                  : row.applicationName}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="left" className="recommended-apps-publisher-cell">
          {row.publisher && (
            <Tooltip title={row.publisher}>
              <Typography className="recommended-apps-publisher-text">
                {row.publisher.length > 24
                  ? `${row.publisher.substring(0, 21)}...`
                  : row.publisher}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="left" className="recommended-apps-date-cell">
          <Typography className="recommended-apps-date-text">
            {formatDateStr(row.startDate)}
          </Typography>
        </TableCell>
        <TableCell align="left" className="recommended-apps-date-cell">
          <Box className="recommended-apps-end-date-wrapper">
            <Typography className="recommended-apps-date-text">
              {formatDateStr(row.endDate)}
            </Typography>
            <Box className="recommended-apps-row-controls">
              <IconButton
                aria-label="row actions"
                size="small"
                onClick={handleClick}
              >
                <MoreVertIcon
                  id="demo-customized-button"
                  aria-controls={openMenu ? "demo-customized-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openMenu ? "true" : undefined}
                />
              </IconButton>
              <StyledMenu
                id="demo-customized-menu"
                slotProps={{
                  list: {
                    "aria-labelledby": "demo-customized-button",
                  },
                }}
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleCloseMenu}
              >
                <MenuItem
                  onClick={() => {
                    onEdit(row);
                    handleCloseMenu();
                  }}
                  disableRipple
                  disabled={
                    sessionStorage.getItem("readonly_OemAdmin") === "true"
                  }
                >
                  <EditIcon />
                  Edit
                </MenuItem>
                <Divider />
                {isPinned && (
                  <>
                    <MenuItem
                      onClick={() => {
                        onTogglePin(row.id);
                        handleCloseMenu();
                      }}
                      disableRipple
                      disabled={
                        sessionStorage.getItem("readonly_OemAdmin") === "true"
                      }
                    >
                      <PushPinIcon className="recommended-apps-pin-icon recommended-apps-pin-icon-active" />
                      Unpin
                    </MenuItem>
                    <Divider />
                  </>
                )}
                <MenuItem
                  onClick={() => {
                    onDelete(row);
                    handleCloseMenu();
                  }}
                  disableRipple
                  sx={{ color: "red" }}
                  disabled={
                    sessionStorage.getItem("readonly_OemAdmin") === "true"
                  }
                >
                  <DeleteForeverOutlinedIcon style={{ color: "red" }} />
                  Delete
                </MenuItem>
              </StyledMenu>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setOpen(!open)}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow className="recommended-apps-collapse-row">
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={isPinned ? 7 : 6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ padding: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                Selected Countries
              </Typography>
              {countriesArr && countriesArr.length > 0 && (
                <>
                  {countriesArr.length > 14 && (
                    <div>
                      {!openMoreCountry && (
                        <>
                          {countriesArr.slice(0, 14).map((countryCode) => (
                            <Chip
                              key={countryCode}
                              sx={{
                                margin: ".5%",
                                borderRadius: "5px !important",
                              }}
                              label={getCountryName(countryCode)}
                            />
                          ))}
                          <Chip
                            sx={{
                              margin: ".5%",
                              borderRadius: "5px !important",
                              cursor: "pointer",
                            }}
                            label={countriesArr.length - 15 + " More"}
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                              setOpenMoreCountry(!openMoreCountry);
                            }}
                          />
                        </>
                      )}
                      {openMoreCountry && (
                        <>
                          {countriesArr.map((countryCode) => (
                            <Chip
                              key={countryCode}
                              sx={{
                                margin: ".5%",
                                borderRadius: "5px !important",
                              }}
                              label={getCountryName(countryCode)}
                            />
                          ))}
                          <Chip
                            sx={{
                              margin: ".5%",
                              borderRadius: "5px !important",
                              cursor: "pointer",
                            }}
                            label="Show Less"
                            color="primary"
                            variant="outlined"
                            onClick={() => {
                              setOpenMoreCountry(!openMoreCountry);
                            }}
                          />
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
              {countriesArr &&
                countriesArr.length > 0 &&
                countriesArr.length <= 14 && (
                  <>
                    {countriesArr.map((countryCode) => (
                      <Chip
                        key={countryCode}
                        sx={{ margin: ".5%", borderRadius: "5px !important" }}
                        label={getCountryName(countryCode)}
                      />
                    ))}
                  </>
                )}
            </Box>
            <Box sx={{ margin: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                ECU Types
              </Typography>
              {row.recommendedApps.appSearchInfo.ecuTypes &&
                row.recommendedApps.appSearchInfo.ecuTypes.length > 0 &&
                row.ecuTypes.length > 0 && (
                  <div>
                    {row.recommendedApps.appSearchInfo.ecuTypes.map(
                      (ecuType) => (
                        <Chip
                          key={ecuType}
                          sx={{
                            margin: ".5%",
                            borderRadius: "5px !important",
                          }}
                          label={ecuType}
                        />
                      ),
                    )}
                  </div>
                )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

Row.propTypes = {
  row: PropTypes.object,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  getCountryName: PropTypes.func,
  isPinned: PropTypes.bool,
  onTogglePin: PropTypes.func,
};

export default function RecommendedApps() {
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(true);
  const [rows, setRows] = React.useState([]);
  const [portfolio, setPortfolio] = useState("");
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("startDate");
  const [openFilter, setOpenFilter] = useState(false);
  const countries = useSelector((state) => state.oemAdminCountires.countries);
  const memoizedCountries = useMemo(() => countries, [countries]);
  const ecuTypeData = useSelector((state) => state.oemAdminEcuTypes.ecu);
  const memoizedEcu = useMemo(() => ecuTypeData, [ecuTypeData]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedEcus, setSelectedEcus] = useState([]);
  const [selectedAppType, setSelectedAppType] = useState("ALL");
  const [selectedAppStatus, setSelectedAppStatus] = useState("ALL");
  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [openSetRecommendedAppModal, setOpenSetRecommendedAppModal] =
    useState(false);
  const [openEditRecommendedAppModal, setOpenEditRecommendedAppModal] =
    useState(false);
  const [openDeleteRecommendedAppModal, setOpenDeleteRecommendedAppModal] =
    useState(false);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [columns, setColumns] = useState([]);
  const [columnsEdit, setColumnsEdit] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllEdit, setSelectAllEdit] = useState(false);
  const [ecuColumns, setEcuColumns] = useState([]);
  const [ecuColumnsEdit, setEcuColumnsEdit] = useState([]);
  const [ecuSelectAll, setEcuSelectAll] = useState(false);
  const [ecuSelectAllEdit, setEcuSelectAllEdit] = useState(false);
  const selectAllText = selectAll ? "Deselect All" : "Select All";
  const selectAllTextECU = ecuSelectAll ? "Deselect All" : "Select All";
  const selectAllTextEdit = selectAllEdit ? "Deselect All" : "Select All";
  const selectAllTextECUEdit = ecuSelectAllEdit ? "Deselect All" : "Select All";
  const [openApp, setOpenApp] = useState(false);
  const [optionsApp, setOptionsApp] = useState([]);
  const [searchdApps, setSearchdApps] = useState([]);
  const [loadingApp, setLoadingApp] = useState(false);
  const currentDate = new Date();
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(addDays(currentDate, 1));
  const [startDateEdit, setStartDateEdit] = useState(currentDate);
  const [endDateEdit, setEndDateEdit] = useState(addDays(currentDate, 1));
  const [selectedAppAsRecommended, setSelectedAppAsRecommended] = useState("");
  const [disableSetRecommendedButton, setDisableSetRecommendedButton] =
    useState(true);
  const [disableEditRecommendedButton, setDisableEditRecommendedButton] =
    useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [selectedRowToAct, setSelectedRowToAct] = useState({});
  const [availableCountriesForEdit, setAvailableCountriesForEdit] = useState(
    [],
  );
  const [availableECUForEdit, setAvailableECUForEdit] = useState([]);
  const [pinnedAppIds, setPinnedAppIds] = useState([]);
  const rowsById = useMemo(
    () => new Map(rows.map((row) => [row.id, row])),
    [rows],
  );

  useEffect(() => {
    setPinnedAppIds((prev) => prev.filter((id) => rowsById.has(id)));
  }, [rowsById]);

  const pinnedRows = useMemo(
    () => pinnedAppIds.map((id) => rowsById.get(id)).filter(Boolean),
    [pinnedAppIds, rowsById],
  );
  const unpinnedRows = useMemo(() => {
    const pinnedIdSet = new Set(pinnedAppIds);
    return rows
      .filter((row) => !pinnedIdSet.has(row.id))
      .sort(getComparator(order, orderBy));
  }, [rows, pinnedAppIds, order, orderBy]);

  const handleTogglePin = (row) => {
    const nextPinnedAppIds = pinnedAppIds.includes(row)
      ? pinnedAppIds.filter((id) => id !== row)
      : [...pinnedAppIds, row];

    const nextPinnedPackageIds = nextPinnedAppIds
      .map((appId) => rowsById.get(appId)?.packageId)
      .filter(Boolean);


    setPinnedAppIds(nextPinnedAppIds);

    if (portfolio) {
      callTheReordersAPI(nextPinnedPackageIds);
    }
  };

  const callTheReordersAPI = (newPinnedAppIds) => {
    APIManager.putRecommendedAppsOrder(portfolio, {
      apps: newPinnedAppIds,
    }).catch((error) => {
      console.error("Failed to update recommended apps order:", error);
    });
  };

  const handlePinnedAppsDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    setPinnedAppIds((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);

      if (oldIndex < 0 || newIndex < 0) {
        return prev;
      }

      const reordered = arrayMove(prev, oldIndex, newIndex);
      const packageIds = reordered
        .map((id) => rowsById.get(id)?.packageId)
        .filter(Boolean);
      callTheReordersAPI(packageIds);
      return reordered;
    });
  };

  const handleCloseApp = () => {
    setOpenApp(false);
    setOptionsApp([]);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let disableButton = true;
    if (selectedAppAsRecommended !== "") {
      let selectedApp = searchdApps.find(
        (e) => e.title === selectedAppAsRecommended,
      );
      if (selectedApp) {
        disableButton = false;
      }
    }
    if (!disableButton && startDate && endDate) {
      if (endDate.getTime() > startDate.getTime()) {
        disableButton = false;
      } else {
        disableButton = true;
      }
    } else {
      disableButton = true;
    }
    setDisableSetRecommendedButton(disableButton);
  }, [selectedAppAsRecommended, startDate, endDate]);

  const handleToggleSelectAll = () => {
    setSelectAll((prev) => {
      if (prev) setColumns([]);
      else setColumns([...memoizedCountries]);
      return !prev;
    });
  };

  const handleToggleSelectAllEdit = () => {
    setSelectAllEdit((prev) => {
      if (prev) setColumnsEdit([]);
      else setColumnsEdit([...availableCountriesForEdit]);
      return !prev;
    });
  };

  const handleToggleSelectAllECU = () => {
    setEcuSelectAll((prev) => {
      if (prev) setEcuColumns([]);
      else setEcuColumns([...memoizedEcu]);
      return !prev;
    });
  };

  const handleToggleSelectAllECUEdit = () => {
    setEcuSelectAllEdit((prev) => {
      if (prev) setEcuColumnsEdit([]);
      else setEcuColumnsEdit([...availableECUForEdit]);
      return !prev;
    });
  };

  const handleFilterApiCalls = (data) => {
    setSelectedEcus(data.selectedEcus);
    setSelectedCountries(data.selectedCountries);
    setSelectedAppType(data.selectedType);
    setSelectedAppStatus(data.selectedStatus || "ALL");
    setOpenFilter(false);
  };

  function handleSetRecommendedAppModalOpen() {
    setColumns([]);
    setEcuColumns([]);
    setOpenSetRecommendedAppModal(true);
  }

  const handleClose = () => {
    setOpenSetRecommendedAppModal(false);
  };

  const handleCloseEdit = () => {
    setSelectedRowToAct([]);
    setOpenEditRecommendedAppModal(false);
  };

  const handleCloseDelete = () => {
    setOpenDeleteRecommendedAppModal(false);
  };

  function handleDataFromChild(data) {
    setPortfolio(data);
  }

  useEffect(() => {
    dispatch(fetchAdminCountriesDetails());
    dispatch(fetchOemAdminEcuDetails());
    getRecommendedApps();
  }, [
    portfolio,
    selectedAppType,
    selectedCountries,
    selectedEcus,
    selectedAppStatus,
  ]);

  function getRecommendedApps() {
    setLoader(true);
    setRows([]);
    if (portfolio && portfolio !== "") {
      APIManager.getRecommendedApps(portfolio)
        .then((json) => {
          if (json && json.length > 0) {
            let filteredContent = json;
            if (selectedAppType !== "" && selectedAppType !== "ALL") {
              const isOemApp = selectedAppType === "OEM";
              filteredContent = filteredContent.filter(
                (item) => item.isOemApp === isOemApp,
              );
            }
            const selectedCountryNames = selectedCountries.map(
              (c) => c.iso3Code,
            );
            if (selectedCountryNames?.length > 0) {
              filteredContent = filteredContent.filter((item) =>
                itemMatchesAnyCountry(item, selectedCountryNames),
              );
            }
            if (selectedEcus.length > 0) {
              filteredContent = filteredContent.filter((item) =>
                itemMatchesAnyEcu(item, selectedEcus),
              );
            }
            if (selectedAppStatus !== "ALL") {
              filteredContent = filteredContent.filter((item) =>
                itemMatchesStatus(item, selectedAppStatus),
              );
            }
            formTableWithData(filteredContent);
          }

          setLoader(false);
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error",
          );
          setOpenErrorPopup(true);
          setLoader(false);
        });
    }
  }

  function getCountryName(code) {
    let country = countries.find((e) => e.iso3Code === code);
    return country ? country.name : code;
  }

  const handleRequestSort = (event, property) => {
    if (property === "pin" || property === "drag") {
      return;
    }

    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const formTableWithData = (apps) => {
    let allRows = [];
    apps.forEach((app) => {
      allRows.push(
        createData({
          id: app.id,
          icon: app.icon,
          applicationName: app.title,
          publisher: app?.seller?.contactFirstName,
          startDate: app.recommendedApps.startDate,
          endDate: app.recommendedApps.endDate,
          countries: app.countries,
          ecuTypes: app.ecuTypes,
          recommendedApps: app.recommendedApps,
          packageId: app.packageId,
        }),
      );
    });

    const orderedPinnedIds = allRows
      .filter((row) => getPinnedOrderValue(row) !== null)
      .sort((a, b) => getPinnedOrderValue(a) - getPinnedOrderValue(b))
      .map((row) => row.id);

    setRows(allRows);
    setPinnedAppIds(orderedPinnedIds);
  };

  const toggleDrawer = (newOpen) => () => {
    setOpenFilter(newOpen);
  };

  const searchApp = (str) => {
    if (str && str !== "" && str.length === 2) {
      setLoadingApp(true);
      let selectedCountryArr =
        columns.length > 0 ? getSelectedCountryNames(columns).join() : "ALL";
      let selectedECUArr = ecuColumns.length > 0 ? ecuColumns.join() : "ALL";
      APIManager.searchOEMAdminApps(
        portfolio,
        "en_US",
        encodeURIComponent(str),
        encodeURIComponent(selectedCountryArr),
        encodeURIComponent(selectedECUArr),
      )
        .then((json) => {
          setOptionsApp(json);
          setSearchdApps(json);
          setOpenApp(true);
          setLoadingApp(false);
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error",
          );
          setOpenErrorPopup(true);
          setLoader(false);
        });
    }
    setSelectedAppAsRecommended(str);
  };

  const getSelectedCountryNames = (countries) => {
    let countryNames = countries.map((country) => country.iso3Code);
    return countryNames;
  };

  function openEditRecommendedAppPopUp(row) {
    setAvailableCountriesForEdit(row.countries);
    setAvailableECUForEdit(row.ecuTypes);

    if (row.recommendedApps.appSearchInfo?.countries) {
      const selectedCountries =
        row.recommendedApps.appSearchInfo.countries.includes("ALL")
          ? row.countries
          : row.recommendedApps.appSearchInfo.countries;

      setColumnsEdit(selectedCountries);

      // Check if all available countries are selected
      if (
        row.recommendedApps.appSearchInfo.countries.includes("ALL") ||
        selectedCountries.length === row.countries.length
      ) {
        setSelectAllEdit(true);
      } else {
        setSelectAllEdit(false);
      }
    }

    if (row.recommendedApps.appSearchInfo?.ecuTypes) {
      const selectedEcuTypes =
        row.recommendedApps.appSearchInfo.ecuTypes.includes("ALL")
          ? row.ecuTypes
          : row.recommendedApps.appSearchInfo.ecuTypes;

      setEcuColumnsEdit(selectedEcuTypes);

      // Check if all available ECU types are selected
      if (
        row.recommendedApps.appSearchInfo.ecuTypes.includes("ALL") ||
        selectedEcuTypes.length === row.ecuTypes.length
      ) {
        setEcuSelectAllEdit(true);
      } else {
        setEcuSelectAllEdit(false);
      }
    }

    setStartDateEdit(new Date(row.startDate));
    setEndDateEdit(new Date(row.endDate));
    setDisableEditRecommendedButton(true);
    setOpenEditRecommendedAppModal(true);
  }

  const openDeleteRecommendedAppPopUp = () => {
    setOpenDeleteRecommendedAppModal(true);
  };

  const handleEditRow = (row) => {
    setSelectedRowToAct(row);
    setColumnsEdit([]);
    setEcuColumnsEdit([]);
    setSelectAllEdit(false);
    setEcuSelectAllEdit(false);
    openEditRecommendedAppPopUp(row);
  };

  const handleDeleteRow = (row) => {
    setSelectedRowToAct(row);
    setColumnsEdit([]);
    setEcuColumnsEdit([]);
    setSelectAllEdit(false);
    setEcuSelectAllEdit(false);
    openDeleteRecommendedAppPopUp();
  };

  const handleSave = () => {
    setLoader(true);
    let selectedApp = searchdApps.find(
      (e) => e.title === selectedAppAsRecommended,
    );
    let payload = {
      appSearchInfo: {
        countries:
          columns.length > 0 ? getSelectedCountryNames(columns) : ["ALL"],
        ecuTypes: ecuColumns.length > 0 ? ecuColumns : ["ALL"],
      },
      startDate: startDate.getTime(),
      endDate: endDate.getTime(),
    };
    APIManager.patchAddRecommendedApp(portfolio, payload, selectedApp.packageId)
      .then((json) => {
        setLoader(false);
        setOpenSetRecommendedAppModal(false);
        setOpenSnackbar(true);
        setSnackMsg(`App has been added as Recommended App.`);
        getRecommendedApps();
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  };

  const handleEdit = () => {
    setLoader(true);
    console.log("Selected Row to Edit:", selectedRowToAct);
    console.log("Columns Edit:", columnsEdit);
    console.log("ECU Columns Edit:", ecuColumnsEdit);

    const isAllCountriesSelected =
      availableCountriesForEdit.length > 0 &&
      columnsEdit.length === availableCountriesForEdit.length;
    const isAllEcuTypesSelected =
      availableECUForEdit.length > 0 &&
      ecuColumnsEdit.length === availableECUForEdit.length;

    let payload = {
      appSearchInfo: {
        countries: isAllCountriesSelected ? ["ALL"] : columnsEdit,
        ecuTypes: isAllEcuTypesSelected ? ["ALL"] : ecuColumnsEdit,
      },
      startDate: startDateEdit.getTime(),
      endDate: endDateEdit.getTime(),
    };
    APIManager.patchAddRecommendedApp(
      portfolio,
      payload,
      selectedRowToAct.packageId,
    )
      .then((json) => {
        setLoader(false);
        setOpenEditRecommendedAppModal(false);
        setOpenSnackbar(true);
        setSnackMsg(`App has been updated.`);
        getRecommendedApps();
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  };

  const handleDelete = () => {
    APIManager.deleteRecommendedApp(portfolio, selectedRowToAct.packageId)
      .then((json) => {
        setLoader(false);
        setOpenDeleteRecommendedAppModal(false);
        setOpenSnackbar(true);
        setSnackMsg(`Recommended App has been updated.`);
        getRecommendedApps();
      })
      .catch((error) => {
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
        setOpenErrorPopup(true);
        setLoader(false);
      });
  };
console.log("Columns edit length", columnsEdit.length)
  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <Box
        sx={{
          marginTop: "3%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "-webkit-box",
          }}
        >
          <Sidebar
            sendDataToParent={handleDataFromChild}
            page="recommended-apps"
          />
          <Box sx={{ flexGrow: 1, marginTop: "16px", marginLeft: "20px" }}>
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              <Typography sx={{ fontSize: "20px", marginTop: "2%" }}>
                {portfolio} - Portfolio Settings
              </Typography>
            </Box>
            <Divider sx={{ marginTop: "2% !important", width: "89%" }} />
            <Box
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              width="89%"
            >
              <Grid container spacing={2}>
                <Grid size={8}>
                  <Typography sx={{ fontSize: "20px", marginTop: "2%" }}>
                    Recommended Apps Settings
                  </Typography>
                  <Typography
                    sx={{ color: "#717273" }}
                    variant="subtitle2"
                    gutterBottom
                  >
                    Manage the listing of apps in the headunit upfront.
                  </Typography>
                </Grid>
                <Grid
                  display="flex"
                  justifyContent="end"
                  alignItems="end"
                  size={4}
                >
                  <FilterAltOutlinedIcon
                    color="action"
                    className="filterIconCls"
                    sx={{ marginRight: "2%" }}
                    onClick={toggleDrawer(true)}
                  />
                  <Button
                    variant="contained"
                    sx={{ textTransform: "none", maxWidth: "none" }}
                    onClick={handleSetRecommendedAppModalOpen}
                    disabled={
                      sessionStorage.getItem("readonly_OemAdmin") === "true"
                    }
                  >
                    Set Recommended App
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                width: "89%",
              }}
            >
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: "12px",
                      color: "#1f2328",
                    }}
                  >
                    Pinned Apps
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: "100%",
                        letterSpacing: 0,
                        display: "inline",
                        marginLeft: "8px",
                        color: "#717273",
                      }}
                    >
                      These apps are prioritized on the head unit, while the
                      remaining apps are displayed in a randomized order.
                    </Typography>
                  </Typography>
                </div>
                <TableContainer
                  className="recommended-apps-table-container"
                  sx={{ overflowX: "hidden" }}
                >
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={handlePinnedAppsDragEnd}
                  >
                    <Table
                      aria-label="pinned apps table"
                      className="recommended-apps-table"
                    >
                      <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        isPinnedTable={true}
                        rowCount={pinnedRows.length}
                      />
                      <SortableContext
                        items={pinnedRows.map((row) => row.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {pinnedRows.map((row) => (
                            <Row
                              key={row.id}
                              row={row}
                              onEdit={handleEditRow}
                              onDelete={handleDeleteRow}
                              getCountryName={getCountryName}
                              isPinned={true}
                              onTogglePin={(row) => handleTogglePin(row)}
                            />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </DndContext>
                </TableContainer>
                <Box sx={{ marginTop: "24px" }} />
              </>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  marginBottom: "12px",
                  color: "#1f2328",
                }}
              >
                Unpinned Apps
              </Typography>
              <TableContainer className="recommended-apps-table-container">
                <Table
                  aria-label="collapsible table"
                  className="recommended-apps-table"
                >
                  <EnhancedTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    isPinnedTable={false}
                    rowCount={unpinnedRows.length}
                  />
                  <TableBody>
                    {unpinnedRows.map((row) => (
                      <Row
                        key={row.id}
                        row={row}
                        onEdit={handleEditRow}
                        onDelete={handleDeleteRow}
                        getCountryName={getCountryName}
                        isPinned={false}
                        onTogglePin={(row) => handleTogglePin(row)}
                      />
                    ))}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="recommended-apps-empty-state"
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
          <SettingsFilterOemAdmin
            openFilter={openFilter}
            toggleDrawer={toggleDrawer}
            selectedCountries={memoizedCountries}
            selectedEcus={memoizedEcu}
            selectedStatus={selectedAppStatus}
            selectedAppType={selectedAppType}
            handleFilterApiCalls={handleFilterApiCalls}
          />
          <Modal
            open={openSetRecommendedAppModal}
            aria-labelledby="parent-modal-title"
            aria-describedby="parent-modal-description"
          >
            <Box sx={createCenteredModalStyle(583)}>
              <div className={styles.modalHeaderCls}>
                <h2 className={styles.modalHeadingCls}>
                  Set App as Recommended
                </h2>
                <IconButton onClick={handleClose}>
                  <ClearIcon />
                </IconButton>
              </div>
              <div>&nbsp;</div>
              <div>
                <form>
                  <div className="recommended-apps-form-row">
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <FormControl
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <label htmlFor="country-autocomplete-set">
                            Country
                          </label>
                          <Autocomplete
                            multiple
                            id="country-autocomplete-set"
                            options={memoizedCountries}
                            disableCloseOnSelect
                            freeSolo={false}
                            limitTags={1}
                            value={columns}
                            getOptionLabel={(option) => option.name}
                            renderTags={(value, getTagProps) => {
                              if (value.length === 0) return [];

                              // Show the first country name
                              const firstName = value[0]?.name || "";

                              if (value.length === 1) {
                                return [
                                  <Chip
                                    {...getTagProps({ index: 0 })}
                                    key={value[0]?.iso3Code}
                                    label={firstName}
                                  />,
                                ];
                              }

                              return [
                                <Chip
                                  {...getTagProps({ index: 0 })}
                                  key={value[0]?.iso3Code}
                                  label={`${firstName} +${value.length - 1}`}
                                />,
                              ];
                            }}
                            renderOption={(
                              renderProps,
                              option,
                              { selected },
                            ) => {
                              const { key, ...optionProps } = renderProps;
                              return (
                                <li key={key} {...optionProps}>
                                  <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                  />
                                  {option.name}
                                </li>
                              );
                            }}
                            onChange={(_e, value, reason) => {
                              if (
                                reason === "clear" ||
                                reason === "removeOption"
                              )
                                setSelectAll(false);
                              if (
                                reason === "selectOption" &&
                                value.length === memoizedCountries.length
                              )
                                setSelectAll(true);
                              setColumns(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                sx={{
                                  "& .MuiInputBase-root": {
                                    padding: 0,
                                  },
                                }}
                                {...params}
                                placeholder={columns.length === 0 ? "All" : ""}
                                className="recommended-apps-autocomplete-input"
                              />
                            )}
                            slots={{ paper: SelectAllPaperComponent }}
                            slotProps={{
                              paper: {
                                hasOptions: memoizedCountries.length > 0,
                                onToggleSelectAll: handleToggleSelectAll,
                                selectAllLabel: selectAllText,
                                isSelectAll: selectAll,
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid size={6}>
                        <FormControl
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <label htmlFor="ecu-autocomplete-set">ECU Type</label>

                          <Autocomplete
                            multiple
                            id="ecu-autocomplete-set"
                            options={memoizedEcu}
                            disableCloseOnSelect
                            freeSolo={false}
                            limitTags={1}
                            value={ecuColumns}
                            getOptionLabel={(option) => option}
                            renderTags={(value, getTagProps) => {
                              if (value.length === 0) return [];

                              // Show the first ECU type name
                              const firstName = value[0];

                              if (value.length === 1) {
                                return [
                                  <Chip
                                    {...getTagProps({ index: 0 })}
                                    key={value[0]}
                                    label={firstName}
                                  />,
                                ];
                              }

                              return [
                                <Chip
                                  {...getTagProps({ index: 0 })}
                                  key={value[0]}
                                  label={`${firstName} +${value.length - 1}`}
                                />,
                              ];
                            }}
                            renderOption={(
                              renderProps,
                              option,
                              { selected },
                            ) => {
                              const { key, ...optionProps } = renderProps;
                              return (
                                <li key={key} {...optionProps}>
                                  <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                  />
                                  {option}
                                </li>
                              );
                            }}
                            onChange={(_e, value, reason) => {
                              if (
                                reason === "clear" ||
                                reason === "removeOption"
                              )
                                setEcuSelectAll(false);
                              if (
                                reason === "selectOption" &&
                                value.length === memoizedEcu.length
                              )
                                setEcuSelectAll(true);
                              setEcuColumns(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                sx={{
                                  "& .MuiInputBase-root": {
                                    padding: 0,
                                  },
                                }}
                                {...params}
                                placeholder={
                                  ecuColumns.length === 0 ? "All" : ""
                                }
                                className="recommended-apps-autocomplete-input"
                              />
                            )}
                            slots={{ paper: SelectAllPaperComponent }}
                            slotProps={{
                              paper: {
                                hasOptions: memoizedEcu.length > 0,
                                onToggleSelectAll: handleToggleSelectAllECU,
                                selectAllLabel: selectAllTextECU,
                                isSelectAll: ecuSelectAll,
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </div>
                  <div className="recommended-apps-form-row">
                    <p className="recommended-form-field-label">Apps</p>
                    <Autocomplete
                      open={openApp}
                      onClose={handleCloseApp}
                      getOptionLabel={(option) => option.title}
                      options={optionsApp}
                      loading={loadingApp}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {loadingApp ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </React.Fragment>
                              ),
                            },
                          }}
                        />
                      )}
                      onInputChange={(event, newInputValue) => {
                        searchApp(newInputValue);
                      }}
                    />
                  </div>
                  <div className="recommended-apps-form-row">
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <p className="recommended-form-field-label">
                          Start Date
                        </p>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label=""
                            disablePast={true}
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid size={6}>
                        <p className="recommended-form-field-label">End Date</p>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label=""
                            disablePast={true}
                            value={endDate}
                            minDate={addDays(startDate, 1)}
                            onChange={(newValue) => setEndDate(newValue)}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  </div>

                  <div
                    className="recommended-apps-form-row"
                    style={{ float: "right", marginTop: "7%" }}
                  >
                    <Button
                      style={{
                        background: "#1214151A",
                        textTransform: "none",
                        height: "36px",
                        width: "116px",
                        color: "#2A2C2D",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                      }}
                      variant="contained"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      sx={{
                        background: "221.2 83.2% 53.3%",
                        textTransform: "none",
                        height: "36px",
                        maxWidth: "none",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                      }}
                      variant="contained"
                      type="button"
                      disabled={disableSetRecommendedButton}
                      onClick={handleSave}
                    >
                      Set as Recommended
                    </Button>
                  </div>
                </form>
              </div>
            </Box>
          </Modal>
          <Modal
            open={openEditRecommendedAppModal}
            aria-labelledby="parent-modal-title"
            aria-describedby="parent-modal-description"
          >
            <Box sx={createCenteredModalStyle(583)}>
              <div className={styles.modalHeaderCls}>
                <h2 className={styles.modalHeadingCls}>Edit Recommended App</h2>
                <IconButton onClick={handleCloseEdit}>
                  <ClearIcon />
                </IconButton>
              </div>
              <div>&nbsp;</div>
              <div>
                <form>
                  <div className="recommended-apps-form-row">
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <FormControl
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <label htmlFor="country-autocomplete-edit">
                            Country
                          </label>
                          <Autocomplete
                            multiple
                            id="country-autocomplete-edit"
                            options={availableCountriesForEdit}
                            disableCloseOnSelect
                            freeSolo={false}
                            limitTags={1} // Change from 0 to 1 to show the first country name
                            value={columnsEdit}
                            getOptionLabel={(option) => {
                              const country = memoizedCountries.find(
                                (e) => e.iso3Code === option,
                              );
                              return country ? country.name : option;
                            }}
                            renderTags={(value, getTagProps) => {
                              const firstCountry = memoizedCountries.find(
                                (e) => e.iso3Code === value[0],
                              );
                              const firstName = firstCountry
                                ? firstCountry.name
                                : value[0];

                              if (value.length <= 1) {
                                return value.map((option, index) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    label={firstName}
                                  />
                                ));
                              }

                              return [
                                <Chip
                                  {...getTagProps({ index: 0 })}
                                  key={value[0]}
                                  label={`${firstName} +${value.length - 1}`}
                                />,
                              ];
                            }}
                            renderOption={(
                              renderProps,
                              option,
                              { selected },
                            ) => {
                              const { key, ...optionProps } = renderProps;
                              const countryName =
                                memoizedCountries.find(
                                  (e) => e.iso3Code === option,
                                )?.name || option;

                              return (
                                <li key={key} {...optionProps}>
                                  <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={
                                      selected || columnsEdit.includes(option)
                                    }
                                  />
                                  {countryName}
                                </li>
                              );
                            }}
                            onChange={(_e, value, reason) => {
                              if (value && value.length > 0) {
                                setDisableEditRecommendedButton(false);
                              } else {
                                setDisableEditRecommendedButton(true);
                              }

                              if (
                                reason === "clear" ||
                                reason === "removeOption"
                              ) {
                                setSelectAllEdit(false);
                              }
                              

                              // Check if all available countries are selected
                              if (
                                value.length ===
                                availableCountriesForEdit.length
                              ) {
                                setSelectAllEdit(true);
                              } else {
                                setSelectAllEdit(false);
                              }

                              setColumnsEdit(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                sx={{
                                  "& .MuiInputBase-root": {
                                    padding: 0,
                                  },
                                }}
                                {...params}
                                placeholder={
                                  columnsEdit.length === 0 ? "All" : ""
                                }
                                className="recommended-apps-autocomplete-input"
                              />
                            )}
                            slots={{ paper: SelectAllPaperComponent }}
                            slotProps={{
                              paper: {
                                hasOptions:
                                  availableCountriesForEdit.length > 0,
                                onToggleSelectAll: handleToggleSelectAllEdit,
                                selectAllLabel: selectAllTextEdit,
                                isSelectAll: selectAllEdit,
                                onBeforeToggle: () =>
                                  setDisableEditRecommendedButton(false),
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid size={6}>
                        <FormControl
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <label htmlFor="ecu-autocomplete-edit">
                            ECU Type
                          </label>

                          <Autocomplete
                            multiple
                            id="ecu-autocomplete-edit"
                            options={availableECUForEdit}
                            disableCloseOnSelect
                            freeSolo={false}
                            limitTags={1} // Change from 0 to 1
                            value={ecuColumnsEdit}
                            getOptionLabel={(option) => option}
                            renderTags={(value, getTagProps) => {
                              if (value.length === 0) return [];

                              // Show the first ECU type name
                              const firstName = value[0];

                              if (value.length === 1) {
                                return [
                                  <Chip
                                    {...getTagProps({ index: 0 })}
                                    key={value[0]}
                                    label={firstName}
                                  />,
                                ];
                              }

                              return [
                                <Chip
                                  {...getTagProps({ index: 0 })}
                                  key={value[0]}
                                  label={`${firstName} +${value.length - 1}`}
                                />,
                              ];
                            }}
                            renderOption={(
                              renderProps,
                              option,
                              { selected },
                            ) => {
                              const { key, ...optionProps } = renderProps;
                              return (
                                <li key={key} {...optionProps}>
                                  <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={
                                      selected ||
                                      ecuColumnsEdit.includes(option)
                                    }
                                  />
                                  {option}
                                </li>
                              );
                            }}
                            onChange={(_e, value, reason) => {
                              if (value && value.length > 0)
                                setDisableEditRecommendedButton(false);

                              if (
                                reason === "clear" ||
                                reason === "removeOption"
                              ) {
                                setEcuSelectAllEdit(false);
                              }

                              // Check if all available ECU types are selected
                              if (value.length === availableECUForEdit.length) {
                                setEcuSelectAllEdit(true);
                              } else {
                                setEcuSelectAllEdit(false);
                              }

                              setEcuColumnsEdit(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                sx={{
                                  "& .MuiInputBase-root": {
                                    padding: 0,
                                  },
                                }}
                                {...params}
                                placeholder={
                                  ecuColumnsEdit.length === 0 ? "All" : ""
                                }
                                className="recommended-apps-autocomplete-input"
                              />
                            )}
                            slots={{ paper: SelectAllPaperComponent }}
                            slotProps={{
                              paper: {
                                hasOptions: availableECUForEdit.length > 0,
                                onToggleSelectAll: handleToggleSelectAllECUEdit,
                                selectAllLabel: selectAllTextECUEdit,
                                isSelectAll: ecuSelectAllEdit,
                                onBeforeToggle: () =>
                                  setDisableEditRecommendedButton(false),
                              },
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </div>
                  <div className="recommended-apps-form-row">
                    <p className="recommended-form-field-label">Apps</p>
                    <p style={{ fontSize: "small" }}>
                      {selectedRowToAct.applicationName}
                    </p>
                  </div>
                  <div className="recommended-apps-form-row">
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <p className="recommended-form-field-label">
                          Start Date
                        </p>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label=""
                            onChange={(newValue) => {
                              setStartDateEdit(newValue);
                              if (endDateEdit.getTime() > newValue.getTime()) {
                                setDisableEditRecommendedButton(false);
                              } else {
                                setDisableEditRecommendedButton(true);
                              }
                            }}
                            value={startDateEdit}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid size={6}>
                        <p className="recommended-form-field-label">End Date</p>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            label=""
                            value={endDateEdit}
                            minDate={addDays(startDateEdit, 1)}
                            onChange={(newValue) => {
                              setEndDateEdit(newValue);
                              if (
                                newValue.getTime() > startDateEdit.getTime()
                              ) {
                                setDisableEditRecommendedButton(false);
                              } else {
                                setDisableEditRecommendedButton(true);
                              }
                            }}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  </div>

                  <div
                    className="recommended-apps-form-row"
                    style={{ float: "right", marginTop: "7%" }}
                  >
                    <Button
                      style={{
                        background: "#1214151A",
                        textTransform: "none",
                        height: "36px",
                        width: "116px",
                        color: "#2A2C2D",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                      }}
                      variant="contained"
                      onClick={handleCloseEdit}
                    >
                      Cancel
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      sx={{
                        background: "221.2 83.2% 53.3%",
                        textTransform: "none",
                        height: "36px",
                        maxWidth: "none",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                      }}
                      variant="contained"
                      type="button"
                      disabled={
                        disableEditRecommendedButton ||
                        columnsEdit.length === 0 ||
                          ecuColumnsEdit.length === 0
                      }
                      onClick={handleEdit}
                    >
                      Set as Recommended
                    </Button>
                  </div>
                </form>
              </div>
            </Box>
          </Modal>
          <Modal
            open={openDeleteRecommendedAppModal}
            aria-labelledby="parent-modal-title"
            aria-describedby="parent-modal-description"
          >
            <Box sx={createCenteredModalStyle(583)}>
              <div className={styles.modalHeaderCls}>
                <h4 style={{ color: "red" }}>
                  Delete app - {selectedRowToAct.applicationName} from
                  recommended list?
                </h4>
                <CloseIcon
                  onClick={handleCloseDelete}
                  sx={{ cursor: "pointer" }}
                />
              </div>
              <div>
                This action will remove the app from the recommended list
              </div>
              <div style={{ marginTop: "2%" }}>
                That action can not be undone.
              </div>
              <div>
                <form>
                  <div
                    className="recommended-apps-form-row"
                    style={{ float: "right", marginTop: "7%" }}
                  >
                    <Button
                      style={{
                        background: "#1214151A",
                        textTransform: "none",
                        height: "36px",
                        width: "116px",
                        color: "#2A2C2D",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                      }}
                      variant="contained"
                      onClick={handleCloseDelete}
                    >
                      Cancel
                    </Button>
                    &nbsp;&nbsp;
                    <Button
                      sx={{
                        background: "221.2 83.2% 53.3%",
                        textTransform: "none",
                        height: "36px",
                        maxWidth: "none",
                        fontWeight: "500",
                        fontSize: "14px",
                        lineHeight: "16.41px",
                      }}
                      variant="contained"
                      color="error"
                      type="button"
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                  </div>
                </form>
              </div>
            </Box>
          </Modal>
        </Box>
      </Box>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={snackMsg}
      />
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
        onClose={() => setOpenErrorPopup(false)}
      />
    </>
  );
}
